// ═══════════════════════════════════════════════════════════════════
//  useSearchIndex — Build + memoize the global search index.
// ═══════════════════════════════════════════════════════════════════
//
//  Takes already-loaded workflows + atlas as inputs (from the caller's
//  useDocuments / useAtlas hooks) and returns a memoized
//  InvertedIndex. Rebuilds only when the input identities change.
//
//  Phase D / E callers wrap this hook around their data sources.
//
// ═══════════════════════════════════════════════════════════════════

import { useMemo } from 'react';
import type { Atlas } from '@/types/atlas';
import { indexAtlas, indexWorkflow } from '@/lib/search/indexEntities';
import {
  buildInvertedIndex,
  clearSearchCache,
  MAX_INDEX_SIZE,
  type InvertedIndex,
} from '@/lib/search/searchEngine';
import type { SearchableEntity } from '@/types/search';

/** Caller-provided slice of the workflows DB row used by the indexer. */
export interface SearchableWorkflowRow {
  id: string;
  title?: string;
  workflow_name?: string;
  category?: string;
  blocks?: unknown;
  connectors?: unknown;
  groups?: unknown;
  updated_at?: string;
}

export interface UseSearchIndexInput {
  workflows?: SearchableWorkflowRow[] | null;
  atlas?: Atlas | null;
}

export interface UseSearchIndexResult {
  index: InvertedIndex;
  entities: readonly SearchableEntity[];
  isReady: boolean;
  isTruncated: boolean;
  workflowCount: number;
  atlasDocCount: number;
}

/**
 * Memoized search index. Rebuilds only when workflows or atlas
 * identity changes; React Query keeps these stable as long as the
 * underlying data hasn't changed.
 */
export function useSearchIndex(input: UseSearchIndexInput): UseSearchIndexResult {
  const { workflows, atlas } = input;

  return useMemo(() => {
    const entities: SearchableEntity[] = [];

    if (workflows && workflows.length) {
      for (const wf of workflows) {
        // Each workflow row may contribute many entities — push in place.
        for (const e of indexWorkflow(wf as unknown as Parameters<typeof indexWorkflow>[0])) {
          entities.push(e);
          if (entities.length >= MAX_INDEX_SIZE) break;
        }
        if (entities.length >= MAX_INDEX_SIZE) break;
      }
    }

    if (atlas && entities.length < MAX_INDEX_SIZE) {
      for (const e of indexAtlas(atlas)) {
        entities.push(e);
        if (entities.length >= MAX_INDEX_SIZE) break;
      }
    }

    const index = buildInvertedIndex(entities);
    // Index changed → drop the per-query LRU cache so stale hits don't leak.
    clearSearchCache();

    return {
      index,
      entities,
      isReady: true,
      isTruncated: index.truncated,
      workflowCount: workflows?.length ?? 0,
      atlasDocCount: atlas?.documents?.length ?? 0,
    };
  }, [workflows, atlas]);
}
